import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Download, 
  Activity, 
  Clock, 
  Users, 
  Target, 
  TrendingUp,
  FileText,
  RefreshCw
} from 'lucide-react';
import { useTrackingContext } from '../../contexts/TrackingContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { studentService } from '../../services/studentService';
import { Student } from '../../types';

export const TrackingDashboard: React.FC = () => {
  const { t } = useTranslation();
  const {
    currentLessonId,
    isTrackingEnabled,
    trackingStats,
    getLessonSummary,
    getLessonEvents,
    exportData,
    clearLessonData
  } = useTrackingContext();

  const [lessonSummary, setLessonSummary] = useState<any>(null);
  const [lessonEvents, setLessonEvents] = useState<any[]>([]);
  const [exportFormat, setExportFormat] = useState<'json' | 'csv' | 'pdf'>('json');
  const [isExporting, setIsExporting] = useState(false);
  const [students, setStudents] = useState<Map<string, Student>>(new Map());

  // Load students and update data periodically
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load students
        const fetchedStudents = await studentService.getStudents();
        const studentMap = new Map<string, Student>();
        fetchedStudents.forEach(student => {
          studentMap.set(student.id, student);
        });
        setStudents(studentMap);

        // Load tracking data if lesson is active
        if (currentLessonId) {
          const [summary, events] = await Promise.all([
            getLessonSummary(),
            getLessonEvents()
          ]);
          setLessonSummary(summary);
          setLessonEvents(events);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [currentLessonId, trackingStats, getLessonSummary, getLessonEvents]);

  const handleExport = async () => {
    if (!currentLessonId) return;

    setIsExporting(true);
    try {
      const data = await exportData(exportFormat);
      
      // Create and download file
      const blob = new Blob([data], { 
        type: exportFormat === 'json' ? 'application/json' : 
              exportFormat === 'csv' ? 'text/csv' : 'application/pdf' 
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lesson-tracking-${currentLessonId}-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearData = async () => {
    if (!currentLessonId) return;
    
    if (window.confirm(t('trackingDashboard.clearDataConfirm'))) {
      try {
        await clearLessonData(currentLessonId);
        setLessonSummary(null);
        setLessonEvents([]);
      } catch (error) {
        console.error('Error clearing lesson data:', error);
      }
    }
  };

  if (!currentLessonId) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="text-center py-8">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('trackingDashboard.trackingDashboard')}</h3>
          <p className="text-sm text-gray-600">{t('trackingDashboard.noActiveSession')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('trackingDashboard.trackingDashboard')}</h3>
              <p className="text-sm text-gray-600">
                {t('trackingDashboard.lesson')}: {currentLessonId} • 
                {t('trackingDashboard.status')}: {isTrackingEnabled ? t('trackingDashboard.active') : t('trackingDashboard.disabled')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={async () => {
                try {
                  const [summary, events] = await Promise.all([
                    getLessonSummary(),
                    getLessonEvents()
                  ]);
                  setLessonSummary(summary);
                  setLessonEvents(events);
                } catch (error) {
                  console.error('Error refreshing data:', error);
                }
              }}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('trackingDashboard.refreshData')}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Real-time Stats */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Activity className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-blue-600">{t('trackingDashboard.totalEvents')}</p>
                <p className="text-xl font-bold text-blue-900">{trackingStats.totalEvents}</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-green-600">{t('trackingDashboard.totalTime')}</p>
                <p className="text-xl font-bold text-green-900">
                  {Math.round(trackingStats.totalTime / 60)}m
                </p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Users className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-sm text-purple-600">{t('trackingDashboard.students')}</p>
                <p className="text-xl font-bold text-purple-900">
                  {lessonSummary?.totalStudents || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <TrendingUp className="w-5 h-5 text-orange-600" />
              <div>
                <p className="text-sm text-orange-600">{t('trackingDashboard.completionRate')}</p>
                <p className="text-xl font-bold text-orange-900">
                  {lessonSummary ? Math.round(lessonSummary.completionRate * 100) : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Lesson Summary */}
        {lessonSummary && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="text-md font-medium text-gray-900 mb-3">{t('trackingDashboard.lessonSummary')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">{t('trackingDashboard.averageTime')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {Math.round(lessonSummary.averageTime / 60)} {t('trackingDashboard.minutes')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('trackingDashboard.averageScore')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {lessonSummary.averageScore ? Math.round(lessonSummary.averageScore) : 0}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('trackingDashboard.engagementRate')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {Math.round(lessonSummary.engagementRate * 100)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('trackingDashboard.lastActivity')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {(() => {
                    try {
                      const lastActivity = trackingStats.lastActivity;
                      if (lastActivity instanceof Date) {
                        return lastActivity.toLocaleTimeString();
                      } else if (lastActivity && typeof lastActivity === 'object' && 'toDate' in lastActivity) {
                        // Firestore Timestamp
                        return (lastActivity as any).toDate().toLocaleTimeString();
                      } else if (typeof lastActivity === 'string') {
                        return new Date(lastActivity).toLocaleTimeString();
                      } else if (typeof lastActivity === 'number') {
                        return new Date(lastActivity).toLocaleTimeString();
                      } else {
                        return 'None';
                      }
                    } catch (error) {
                      return 'None';
                    }
                  })()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Recent Events */}
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-900 mb-3">{t('trackingDashboard.recentEvents')}</h4>
          <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
            {lessonEvents.length > 0 ? (
              <div className="space-y-2">
                {lessonEvents.slice(-10).reverse().map((event, index) => (
                  <div key={event.id || index} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div className="flex items-center space-x-3">
                      <Target className="w-4 h-4 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {event.eventType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </p>
                        <p className="text-xs text-gray-600">
                          {t('trackingDashboard.student')}: {students.get(event.studentId)?.name || event.studentId} • {(() => {
                            try {
                              const timestamp = event.timestamp;
                              if (timestamp instanceof Date) {
                                return timestamp.toLocaleTimeString();
                              } else if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
                                // Firestore Timestamp
                                return (timestamp as any).toDate().toLocaleTimeString();
                              } else if (typeof timestamp === 'string') {
                                return new Date(timestamp).toLocaleTimeString();
                              } else if (typeof timestamp === 'number') {
                                return new Date(timestamp).toLocaleTimeString();
                              } else {
                                return t('trackingDashboard.unknownTime');
                              }
                            } catch (error) {
                              return t('trackingDashboard.invalidTime');
                            }
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">{t('trackingDashboard.noEventsRecorded')}</p>
            )}
          </div>
        </div>

        {/* Export Section */}
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-md font-medium text-gray-900 mb-3">{t('trackingDashboard.exportData')}</h4>
          <div className="flex items-center space-x-4">
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as any)}
              className="input-field w-32"
            >
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
              <option value="pdf">PDF</option>
            </select>
            
            <button
              onClick={handleExport}
              disabled={isExporting || lessonEvents.length === 0}
              className="btn-primary flex items-center space-x-2"
            >
              {isExporting ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {/* <span>{isExporting ? t('trackingDashboard.exporting') : t('trackingDashboard.export')}</span> */}
            </button>

            <button
              onClick={handleClearData}
              className="btn-secondary flex items-center space-x-2"
            >
              <FileText className="w-4 h-4" />
              {/* <span>{t('trackingDashboard.clearData')}</span> */}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
